/**
 * Activity Helpers — Shared utilities for Parent, Teacher & Admin portals
 *
 * Derives activity data from questionStates timestamps.
 * All functions are pure and can be memoized with useMemo.
 *
 * Created as part of Portal Redesign Plan v2.0, Phase 0.
 */

import { type ModuleId, MODULES, MODULE_CODE_TO_ID } from '../content';
import { AppState } from '../app/types';
import type { QuestionState, FamilyMasteryState } from '../mastery';
import { getModuleMasteryFromFamilies } from '../mastery';
import { MS_PER_DAY } from '../config/constants';

// ============================================================
// Types
// ============================================================

export interface DayActivity {
  /** Day label in Dutch (Ma, Di, Wo, Do, Vr, Za, Zo) */
  label: string;
  /** Whether any practice happened on this day */
  practiced: boolean;
  /** Number of questions answered on this day */
  questionCount: number;
  /** Whether this day is today */
  isToday: boolean;
}
export interface PracticeSession {
  /** Start timestamp of the session */
  date: number;
  /** Module practiced (most frequent in session) */
  moduleId: ModuleId | null;
  /** Module display title */
  moduleTitle: string;
  /** Module icon emoji */
  moduleIcon: string;
  /** Total questions in session */
  questionCount: number;
  /** Correct answers in session */
  correctCount: number;
  /** Accuracy percentage 0-100 */
  accuracy: number;
}
export type TrendDirection = 'up' | 'stable' | 'down';
export interface Milestone {
  type: 'level_up' | 'subject_complete' | 'streak' | 'first_practice';
  label: string;
  emoji: string;
  timestamp: number;
}

// ============================================================
// Constants
// ============================================================

const DAY_LABELS = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
const SESSION_GAP_MS = 30 * 60 * 1000; // 30 minutes between sessions

// ============================================================
// Shared Trend Calculation Helper (FS-5)
// ============================================================

/**
 * Calculate accuracy delta between recent and overall performance.
 * Shared helper to eliminate 5× duplicated trend calculation pattern.
 *
 * @returns Percentage point difference (e.g., +3 means 3% improvement), or 0 if insufficient data.
 */
export function calculateAccuracyDelta(recentCorrect: number, recentTotal: number, overallCorrect: number, overallTotal: number, minOverall: number = 5, minRecent: number = 3): number {
  if (overallTotal < minOverall || recentTotal < minRecent) return 0;
  const overallAcc = overallCorrect / overallTotal;
  const recentAcc = recentCorrect / recentTotal;
  return Math.round((recentAcc - overallAcc) * 100);
}

/**
 * Convert a percentage-point delta to a TrendDirection.
 * threshold is in percentage points (default 5).
 */
export function deltaToTrend(delta: number, threshold: number = 5): TrendDirection {
  if (delta > threshold) return 'up';
  if (delta < -threshold) return 'down';
  return 'stable';
}

// ============================================================
// Weekly Activity
// ============================================================

/**
 * Get weekly activity for a learner (Mon-Sun of current week).
 * Derives data from questionStates history timestamps.
 *
 * @returns Array of 7 DayActivity objects (Mon through Sun)
 */
export function getWeeklyActivityForLearner(questionStates: Record<string, QuestionState>): DayActivity[] {
  const now = new Date();
  // Get Monday of current week
  const dayOfWeek = now.getDay(); // 0=Sun, 1=Mon, ...
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(monday.getDate() + mondayOffset);
  const mondayTs = monday.getTime();

  // Initialize 7 days
  const days: DayActivity[] = DAY_LABELS.map((label, index) => {
    const dayDate = new Date(mondayTs + index * MS_PER_DAY);
    const isToday = dayDate.getDate() === now.getDate() && dayDate.getMonth() === now.getMonth() && dayDate.getFullYear() === now.getFullYear();
    return {
      label,
      practiced: false,
      questionCount: 0,
      isToday
    };
  });

  // Scan all question states for activity this week
  for (const state of Object.values(questionStates)) {
    if (!state.history || state.history.length === 0) continue;
    for (const attempt of state.history) {
      const ts = attempt.timestamp;
      if (ts < mondayTs) continue; // Before this week

      const dayIndex = Math.floor((ts - mondayTs) / MS_PER_DAY);
      if (dayIndex >= 0 && dayIndex < 7) {
        days[dayIndex].practiced = true;
        days[dayIndex].questionCount++;
      }
    }
  }
  return days;
}

/**
 * Get total practice days this week.
 */
export function getWeeklyPracticeDayCount(questionStates: Record<string, QuestionState>): number {
  return getWeeklyActivityForLearner(questionStates).filter(d => d.practiced).length;
}

// ============================================================
// Recent Sessions
// ============================================================

/**
 * Get recent practice sessions for a learner.
 * Groups consecutive answers (within 30-min windows) into "sessions".
 *
 * @param questionStates - The learner's question states
 * @param limit - Max sessions to return (default 5)
 * @returns Array of PracticeSession objects, most recent first
 */
export function getRecentSessionsForLearner(questionStates: Record<string, QuestionState>, limit: number = 5): PracticeSession[] {
  // Collect all individual attempts with their question context
  const allAttempts: Array<{
    timestamp: number;
    isCorrect: boolean;
    questionId: string;
    moduleId: ModuleId | null;
  }> = [];
  for (const [key, state] of Object.entries(questionStates)) {
    if (!state.history || state.history.length === 0) continue;

    // Extract base questionId (remove _g6, _g7, _g8 suffix)
    const baseQuestionId = state.questionId || key.replace(/_g\d+$/, '');

    // Determine module from questionId prefix
    const moduleId = getModuleFromQuestionId(baseQuestionId);
    for (const attempt of state.history) {
      allAttempts.push({
        timestamp: attempt.timestamp,
        isCorrect: attempt.isCorrect,
        questionId: baseQuestionId,
        moduleId
      });
    }
  }
  if (allAttempts.length === 0) return [];

  // Sort by timestamp descending
  allAttempts.sort((a, b) => b.timestamp - a.timestamp);

  // Group into sessions (gap > 30 min = new session)
  const sessions: PracticeSession[] = [];
  let currentSession: typeof allAttempts = [allAttempts[0]];
  for (let i = 1; i < allAttempts.length; i++) {
    const gap = currentSession[currentSession.length - 1].timestamp - allAttempts[i].timestamp;
    if (gap > SESSION_GAP_MS) {
      // Finalize current session
      sessions.push(buildSession(currentSession));
      if (sessions.length >= limit) break;
      currentSession = [allAttempts[i]];
    } else {
      currentSession.push(allAttempts[i]);
    }
  }

  // Don't forget the last session
  if (currentSession.length > 0 && sessions.length < limit) {
    sessions.push(buildSession(currentSession));
  }
  return sessions.slice(0, limit);
}
function buildSession(attempts: Array<{
  timestamp: number;
  isCorrect: boolean;
  questionId: string;
  moduleId: ModuleId | null;
}>): PracticeSession {
  // Find most frequent module in session
  const moduleCounts = new Map<string, number>();
  for (const a of attempts) {
    if (a.moduleId) {
      moduleCounts.set(a.moduleId, (moduleCounts.get(a.moduleId) || 0) + 1);
    }
  }
  let topModuleId: ModuleId | null = null;
  let topCount = 0;
  for (const [mod, count] of moduleCounts) {
    if (count > topCount) {
      topModuleId = mod as ModuleId;
      topCount = count;
    }
  }
  const moduleData = topModuleId ? MODULES.find(m => m.id === topModuleId) : null;
  const correctCount = attempts.filter(a => a.isCorrect).length;
  return {
    date: attempts[0].timestamp,
    // Most recent attempt in session
    moduleId: topModuleId,
    moduleTitle: moduleData?.title || 'Gemengd',
    moduleIcon: moduleData?.icon || '📚',
    questionCount: attempts.length,
    correctCount,
    accuracy: attempts.length > 0 ? Math.round(correctCount / attempts.length * 100) : 0
  };
}

/**
 * Derive moduleId from questionId prefix.
 * Question IDs follow pattern: MODULE-FAMILY-VARIANT (e.g., GET-BREUK-BASIS-001)
 */
export function getModuleFromQuestionId(questionId: string): ModuleId | null {
  const prefix = questionId.split('-')[0]?.toUpperCase();
  // Legacy: TGL-TIJD-* → Tijd, TGL-GELD-* → Geld (existing mastery data may still use TGL prefix)
  if (prefix === 'TGL') {
    const secondSegment = questionId.split('-')[1]?.toUpperCase();
    if (secondSegment === 'GELD') return 'Geld';
    return 'Tijd';
  }
  return MODULE_CODE_TO_ID[prefix] || null;
}

// ============================================================
// Mastery Trends
// ============================================================

/**
 * Calculate mastery trend for a module.
 * Compares recent activity (last 7 days) accuracy to overall accuracy.
 *
 * @returns 'up' if recent > overall + 5%, 'down' if recent < overall - 5%, else 'stable'
 */
export function calculateMasteryTrend(familyMastery: Record<string, FamilyMasteryState>, moduleId: ModuleId, group: number): TrendDirection {
  const weekAgo = Date.now() - 7 * MS_PER_DAY;
  let recentCorrect = 0;
  let recentTotal = 0;
  let overallCorrect = 0;
  let overallTotal = 0;
  for (const [key, state] of Object.entries(familyMastery)) {
    const familyModule = getModuleFromQuestionId(state.familyId);
    if (familyModule !== moduleId) continue;
    if (!key.includes(`_g${group}`)) continue;
    overallCorrect += state.correct;
    overallTotal += state.attempts;
    if (state.history) {
      for (const attempt of state.history) {
        if (attempt.timestamp >= weekAgo) {
          recentTotal++;
          if (attempt.isCorrect) recentCorrect++;
        }
      }
    }
  }
  return deltaToTrend(calculateAccuracyDelta(recentCorrect, recentTotal, overallCorrect, overallTotal));
}

/**
 * Calculate per-module trends for all modules.
 */
export function getAllModuleTrends(familyMastery: Record<string, FamilyMasteryState>, group: number): Record<ModuleId, TrendDirection> {
  const result: Partial<Record<ModuleId, TrendDirection>> = {};
  for (const module of MODULES) {
    result[module.id] = calculateMasteryTrend(familyMastery, module.id, group);
  }
  return result as Record<ModuleId, TrendDirection>;
}

// ============================================================
// Class-Level Trend Aggregation (Teacher)
// ============================================================

export interface ClassModuleStat {
  moduleId: ModuleId;
  moduleTitle: string;
  moduleIcon: string;
  avgMastery: number;
  practicedCount: number;
  distribution: {
    excellent: number; // ≥75%
    good: number; // ≥40%
    attention: number; // >0 & <40%
    notStarted: number; // 0
  };
  trend: 'up' | 'down' | 'stable' | 'neutral';
}

/**
 * Calculate class-level trend delta (recent 7-day accuracy vs overall accuracy).
 * Used by Teacher GrowthPage summary card.
 *
 * @returns Integer percentage delta (e.g., +3 means 3% improvement)
 */
export function getClassTrendDelta(students: Array<{
  data: AppState;
}>, group: number): number {
  let recentCorrect = 0;
  let recentTotal = 0;
  let overallCorrect = 0;
  let overallTotal = 0;
  const weekAgo = Date.now() - 7 * MS_PER_DAY;
  for (const student of students) {
    const fm = student.data.familyMastery || {};
    for (const [key, state] of Object.entries(fm)) {
      if (!key.includes(`_g${group}`)) continue;
      overallCorrect += state.correct;
      overallTotal += state.attempts;
      if (state.history) {
        for (const h of state.history) {
          if (h.timestamp >= weekAgo) {
            recentTotal++;
            if (h.isCorrect) recentCorrect++;
          }
        }
      }
    }
  }
  return calculateAccuracyDelta(recentCorrect, recentTotal, overallCorrect, overallTotal, 10, 5);
}

/**
 * Calculate trend delta for a single learner — compares recent week accuracy
 * to overall accuracy across all families for the given group.
 * Used by Teacher MyClassPage grid cards (TM-5).
 *
 * @returns Percentage point difference (positive = improving, negative = declining)
 */
export function getLearnerTrendDelta(familyMastery: Record<string, {
  correct: number;
  attempts: number;
  history?: Array<{
    timestamp: number;
    isCorrect: boolean;
  }>;
}>, group: number): number {
  let recentCorrect = 0;
  let recentTotal = 0;
  let overallCorrect = 0;
  let overallTotal = 0;
  const weekAgo = Date.now() - 7 * MS_PER_DAY;
  for (const [key, state] of Object.entries(familyMastery)) {
    if (!key.includes(`_g${group}`)) continue;
    overallCorrect += state.correct;
    overallTotal += state.attempts;
    if (state.history) {
      for (const h of state.history) {
        if (h.timestamp >= weekAgo) {
          recentTotal++;
          if (h.isCorrect) recentCorrect++;
        }
      }
    }
  }
  return calculateAccuracyDelta(recentCorrect, recentTotal, overallCorrect, overallTotal);
}

/**
 * Calculate trend delta for a single learner for a specific module.
 * Compares recent week accuracy to overall accuracy for that module.
 * Used by Parent GrowthPage (PG-1).
 *
 * @returns Percentage point difference (positive = improving, negative = declining)
 */
export function getModuleTrendDelta(familyMastery: Record<string, {
  correct: number;
  attempts: number;
  history?: Array<{
    timestamp: number;
    isCorrect: boolean;
  }>;
}>, group: number, moduleId: string): number {
  let recentCorrect = 0;
  let recentTotal = 0;
  let overallCorrect = 0;
  let overallTotal = 0;
  const weekAgo = Date.now() - 7 * MS_PER_DAY;
  for (const [key, state] of Object.entries(familyMastery)) {
    if (!key.includes(`_g${group}`)) continue;
    const baseId = key.replace(/_g\d+$/, '');
    const familyModuleId = getModuleFromQuestionId(baseId);
    if (familyModuleId !== moduleId) continue;
    overallCorrect += state.correct;
    overallTotal += state.attempts;
    if (state.history) {
      for (const h of state.history) {
        if (h.timestamp >= weekAgo) {
          recentTotal++;
          if (h.isCorrect) recentCorrect++;
        }
      }
    }
  }
  return calculateAccuracyDelta(recentCorrect, recentTotal, overallCorrect, overallTotal);
}

/**
 * Calculate per-module class-level stats (avg mastery, distribution, trend).
 * Used by Teacher GrowthPage module bars.
 *
 * @returns Array of ClassModuleStat sorted by avgMastery descending
 */
export function getClassModuleStats(students: Array<{
  id: string;
  data: AppState;
}>, group: number, getModuleMastery: (learnerId: string, moduleId: ModuleId) => number): ClassModuleStat[] {
  const weekAgo = Date.now() - 7 * MS_PER_DAY;
  return MODULES.map(module => {
    let sum = 0;
    let count = 0;
    const distribution = {
      excellent: 0,
      good: 0,
      attention: 0,
      notStarted: 0
    };
    let modRecentCorrect = 0;
    let modRecentTotal = 0;
    let modOverallCorrect = 0;
    let modOverallTotal = 0;
    for (const student of students) {
      const mastery = getModuleMastery(student.id, module.id);
      if (mastery > 0) {
        sum += mastery;
        count++;
        if (mastery >= 75) distribution.excellent++;else if (mastery >= 40) distribution.good++;else distribution.attention++;
      } else {
        distribution.notStarted++;
      }
      const fm = student.data.familyMastery || {};
      for (const [key, state] of Object.entries(fm)) {
        if (!key.includes(`_g${group}`)) continue;
        const familyModule = getModuleFromQuestionId(state.familyId);
        if (familyModule !== module.id) continue;
        modOverallCorrect += state.correct;
        modOverallTotal += state.attempts;
        if (state.history) {
          for (const h of state.history) {
            if (h.timestamp >= weekAgo) {
              modRecentTotal++;
              if (h.isCorrect) modRecentCorrect++;
            }
          }
        }
      }
    }
    const modDelta = calculateAccuracyDelta(modRecentCorrect, modRecentTotal, modOverallCorrect, modOverallTotal);
    let trend: 'up' | 'down' | 'stable' | 'neutral' = 'neutral';
    if (modOverallTotal >= 5 && modRecentTotal >= 3) {
      trend = deltaToTrend(modDelta) as 'up' | 'down' | 'stable';
    }
    return {
      moduleId: module.id,
      moduleTitle: module.title,
      moduleIcon: module.icon,
      avgMastery: count > 0 ? Math.round(sum / count) : 0,
      practicedCount: count,
      distribution,
      trend
    };
  }).sort((a, b) => b.avgMastery - a.avgMastery);
}

// ============================================================
// Active Users (Admin)
// ============================================================

/**
 * Count active users within a time window.
 *
 * @param accounts - All accounts
 * @param getLearnerData - Function to get learner data by ID
 * @param hoursWindow - Time window in hours (default 24)
 * @returns Number of learners with activity in the window
 */
export function getActiveUsersCount(accountsById: Record<string, {
  id: string;
  role: string;
}>, getLearnerData: (id: string) => AppState | null, hoursWindow: number = 24): number {
  const cutoff = Date.now() - hoursWindow * 60 * 60 * 1000;
  let count = 0;
  for (const account of Object.values(accountsById)) {
    if (account.role !== 'learner') continue;
    const data = getLearnerData(account.id);
    if (!data?.questionStates) continue;

    // Check if any question was answered after cutoff
    const hasRecentActivity = Object.values(data.questionStates).some(state => state.lastAttemptDate > cutoff);
    if (hasRecentActivity) count++;
  }
  return count;
}

// ============================================================
// Weakest Family in Module (Teacher)
// ============================================================

/**
 * Find the weakest family within a module for a specific learner.
 * Used by AttentionCards to give specific pedagogical hints.
 */
export function getWeakestFamilyInModule(familyMastery: Record<string, FamilyMasteryState>, moduleId: ModuleId, group: number): {
  familyId: string;
  mastery: number;
  label: string;
} | null {
  let weakest: {
    familyId: string;
    mastery: number;
  } | null = null;
  for (const [key, state] of Object.entries(familyMastery)) {
    const familyModule = getModuleFromQuestionId(state.familyId);
    if (familyModule !== moduleId) continue;
    if (!key.includes(`_g${group}`)) continue;
    if (state.attempts < 2) continue; // Need minimum data

    if (!weakest || state.masteryScore < weakest.mastery) {
      weakest = {
        familyId: state.familyId,
        mastery: state.masteryScore
      };
    }
  }
  if (!weakest) return null;

  // Create human-readable label from familyId
  // e.g., "SWW-TT-STAM" → "Tegenwoordige tijd - stamregel"
  const label = humanizeFamilyId(weakest.familyId);
  return {
    ...weakest,
    label
  };
}
function humanizeFamilyId(familyId: string): string {
  // Remove module prefix and join remaining parts
  const parts = familyId.split('-').slice(1);
  return parts.join(' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
}

// ============================================================
// Recent Milestones (Parent)
// ============================================================

/**
 * Detect recent milestones for a learner.
 * Checks for level-ups, subject completions, and practice streaks.
 */
export function getRecentMilestones(learnerData: AppState, group: number): Milestone[] {
  const milestones: Milestone[] = [];
  const now = Date.now();
  const weekAgo = now - 7 * MS_PER_DAY;

  // Check for high mastery subjects (≥95% = "Klaar!")
  const familyMastery = learnerData.familyMastery || {};
  for (const module of MODULES) {
    const mastery = getModuleMasteryFromFamilies(familyMastery, module.id, group);
    if (mastery >= 95) {
      milestones.push({
        type: 'subject_complete',
        label: `${module.title} is klaar! ⭐`,
        emoji: '🏆',
        timestamp: now // We don't track exact completion time
      });
    }
  }

  // Check for practice streak (consecutive days this week)
  const weeklyActivity = getWeeklyActivityForLearner(learnerData.questionStates || {});
  let currentStreak = 0;
  let maxStreak = 0;
  for (const day of weeklyActivity) {
    if (day.practiced) {
      currentStreak++;
      maxStreak = Math.max(maxStreak, currentStreak);
    } else {
      currentStreak = 0;
    }
  }
  if (maxStreak >= 3) {
    milestones.push({
      type: 'streak',
      label: `${maxStreak} dagen achter elkaar geoefend!`,
      emoji: '🔥',
      timestamp: now
    });
  }

  // Check for any practice this week (first practice milestone)
  const totalThisWeek = weeklyActivity.reduce((sum, d) => sum + d.questionCount, 0);
  if (totalThisWeek > 0 && totalThisWeek <= 10) {
    milestones.push({
      type: 'first_practice',
      label: `${totalThisWeek} vragen beantwoord deze week`,
      emoji: '📝',
      timestamp: now
    });
  }
  return milestones;
}

// ============================================================
// Weekly Stats Summary (for WeeklyHighlights)
// ============================================================

export interface WeeklyStats {
  totalQuestions: number;
  practiceDays: number;
  correctAnswers: number;
  accuracy: number;
  bestModuleId: ModuleId | null;
  bestModuleTitle: string;
  bestModuleIcon: string;
  bestModuleCount: number;
}

/**
 * Get weekly practice statistics for a learner.
 */
export function getWeeklyStats(questionStates: Record<string, QuestionState>): WeeklyStats {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(monday.getDate() + mondayOffset);
  const mondayTs = monday.getTime();
  const moduleCounts = new Map<ModuleId, number>();
  const practiceDays = new Set<number>();
  let totalQuestions = 0;
  let correctAnswers = 0;
  for (const [key, state] of Object.entries(questionStates)) {
    if (!state.history) continue;
    const baseId = state.questionId || key.replace(/_g\d+$/, '');
    const moduleId = getModuleFromQuestionId(baseId);
    for (const attempt of state.history) {
      if (attempt.timestamp < mondayTs) continue;
      totalQuestions++;
      if (attempt.isCorrect) correctAnswers++;
      const dayIndex = Math.floor((attempt.timestamp - mondayTs) / MS_PER_DAY);
      practiceDays.add(dayIndex);
      if (moduleId) {
        moduleCounts.set(moduleId, (moduleCounts.get(moduleId) || 0) + 1);
      }
    }
  }

  // Find best module
  let bestModuleId: ModuleId | null = null;
  let bestCount = 0;
  for (const [mod, count] of moduleCounts) {
    if (count > bestCount) {
      bestModuleId = mod;
      bestCount = count;
    }
  }
  const bestModule = bestModuleId ? MODULES.find(m => m.id === bestModuleId) : null;
  const accuracy = totalQuestions > 0 ? Math.round(correctAnswers / totalQuestions * 100) : 0;
  return {
    totalQuestions,
    practiceDays: practiceDays.size,
    correctAnswers,
    accuracy,
    bestModuleId,
    bestModuleTitle: bestModule?.title || '',
    bestModuleIcon: bestModule?.icon || '',
    bestModuleCount: bestCount
  };
}